// src/components/DialogModal.jsx
import React, { useEffect, useMemo, useState, useRef } from "react";
import ModalBackdrop from "components/ModalBackdrop";

export default function DialogModal({ dialog, closeDialog }) {
  // ──────────────────────────────────────────────
  // Hooks MUST be unconditional
  // ──────────────────────────────────────────────
  const hasInput = !!dialog?.input;
  const hasCheckbox = !!dialog?.checkbox;

  const [inputValue, setInputValue] = useState("");
  const [checkboxChecked, setCheckboxChecked] = useState(false);

  // Keep a ref so key handlers always read the latest value without
  // re-binding listeners on every keystroke.
  const inputRef = useRef("");
  const checkboxRef = useRef(false);
  useEffect(() => {
    inputRef.current = inputValue;
  }, [inputValue]);
  useEffect(() => {
    checkboxRef.current = checkboxChecked;
  }, [checkboxChecked]);

  // Initialize input value when dialog changes (or clears)
  useEffect(() => {
    if (!hasInput) {
      setInputValue("");
      inputRef.current = "";
      return;
    }

    const dflt =
      typeof dialog?.input?.defaultValue === "string"
        ? dialog.input.defaultValue
        : "";

    setInputValue(dflt);
    inputRef.current = dflt;
  }, [dialog, hasInput]);

  useEffect(() => {
    const checked = hasCheckbox && dialog?.checkbox?.defaultChecked === true;
    setCheckboxChecked(checked);
    checkboxRef.current = checked;
  }, [dialog, hasCheckbox]);

  const defaultAction = useMemo(() => {
    if (!dialog) return null;

    if (dialog.defaultAction) return dialog.defaultAction;

    const btns = Array.isArray(dialog.buttons) ? dialog.buttons : [];
    if (btns.some((b) => b.value === "yes")) return "yes";
    if (btns.some((b) => b.value === "ok")) return "ok";
    return null;
  }, [dialog]);

  // Key handling (Escape, Enter)
  useEffect(() => {
    if (!dialog) return;

    const handler = (e) => {
      if (e.key === "Escape") {
        closeDialog("cancel");
        return;
      }

      if (e.key === "Enter") {
        if (!defaultAction) return;

        e.preventDefault();

        if (hasInput || hasCheckbox) {
          closeDialog({
            value: defaultAction,
            inputValue: inputRef.current,
            checkboxChecked: checkboxRef.current,
          });
        } else {
          closeDialog(defaultAction);
        }
      }
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [dialog, closeDialog, defaultAction, hasInput, hasCheckbox]);

  // ──────────────────────────────────────────────
  // Render guard AFTER hooks
  // ──────────────────────────────────────────────
  if (!dialog) return null;

  const { title, message, buttons = [], input, checkbox } = dialog;

  const handleClose = (payload) => closeDialog(payload);

  const handleButtonClick = (btn) => {
    if (!btn) return;

    if (hasInput || hasCheckbox) {
      handleClose({
        value: btn.value,
        inputValue: inputRef.current,
        checkboxChecked: checkboxRef.current,
      });
      return;
    }

    handleClose(btn.value);
  };

  return (
    <ModalBackdrop onClose={() => handleClose("cancel")} zIndex="z-[1100]">
      <div
        className="bg-[hsl(var(--background))] text-app rounded-xl shadow-xl border border-border max-w-lg w-full mx-4 p-6"
        role="dialog"
        aria-modal="true"
      >
        {title ? (
          <h2 className="text-base font-semibold mb-3 break-words">{title}</h2>
        ) : null}

        {message ? (
          <div className="mb-5 whitespace-pre-line text-sm leading-relaxed text-app">
            {message}
          </div>
        ) : null}

        {input ? (
          <div className="mb-4">
            {input?.label ? (
              <label className="block text-xs font-medium text-muted mb-1">
                {input.label}
              </label>
            ) : null}

            <input
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={input?.placeholder || ""}
              autoFocus={input?.autoFocus !== false}
              className="h-9 w-full rounded-md border border-input bg-background px-3 text-sm text-app placeholder:text-muted"
              aria-label={input?.ariaLabel || input?.label || "Input"}
            />

            {input?.helpText ? (
              <div className="mt-1 text-xs text-muted">{input.helpText}</div>
            ) : null}
          </div>
        ) : null}

        {checkbox ? (
          <label className="mb-5 flex cursor-pointer items-start gap-2 text-sm text-app">
            <input
              type="checkbox"
              checked={checkboxChecked}
              onChange={(e) => setCheckboxChecked(e.target.checked)}
              className="mt-0.5 h-4 w-4 rounded border-input"
            />
            <span>
              <span className="font-medium">{checkbox.label}</span>
              {checkbox.helpText ? (
                <span className="mt-0.5 block text-xs text-muted">
                  {checkbox.helpText}
                </span>
              ) : null}
            </span>
          </label>
        ) : null}

        <div className="flex flex-wrap justify-end gap-2">
          {buttons.map((btn) => {
            let cls = "btn min-w-[5.5rem]";

            if (btn.variant === "primary") {
              cls += " btn-primary";
            } else if (btn.variant === "ghost") {
              cls += " btn-ghost";
            } else if (btn.variant === "brand-ghost") {
              cls += " btn-brand-ghost";
            } else if (btn.variant === "cancel") {
              cls =
                "btn min-w-[5.5rem] bg-transparent text-app hover:text-brand";
            }

            return (
              <button
                type="button"
                key={btn.value}
                className={cls}
                onClick={() => handleButtonClick(btn)}
              >
                {btn.label}
              </button>
            );
          })}
        </div>
      </div>
    </ModalBackdrop>
  );
}
